import { useAppTheme } from "@hooks/useAppTheme";
import { MarkdownRenderer, TooltipWrapper } from "@memorang/ui";
import { StyleSheet } from "react-native";
import { DataTable, Text } from "react-native-paper";
import type { EMRData, OLDCART, UserEMRData, Vitals } from "./types";
import { defaultGoldStandardData } from "./useEMRStore";

type Props = {
	data: Partial<EMRData> | Partial<UserEMRData>;
	goldStandardData?: Partial<EMRData>;
	isUserData?: boolean;
	isAnswered?: boolean;
	currentItemGoldStandard?: Partial<EMRData>;
	showTooltip?: boolean;
};

const EMRTable = ({
	data,
	goldStandardData,
	isUserData = false,
	isAnswered,
	currentItemGoldStandard,
	showTooltip,
}: Props) => {
	// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
	const renderVitals = (vitals: Vitals | null | undefined) => {
		if (!vitals) {
			return "--";
		}

		const parts: string[] = [];
		if (vitals.HR && vitals.HR !== 0) {
			parts.push(`**HR**: ${vitals.HR}`);
		}
		if (vitals.BP && vitals.BP.trim() !== "") {
			parts.push(`**BP**: ${vitals.BP}`);
		}
		if (vitals.RR && vitals.RR !== 0) {
			parts.push(`**RR**: ${vitals.RR}`);
		}
		if (vitals.SpO2 && vitals.SpO2 !== 0) {
			parts.push(`**SpO2**: ${vitals.SpO2}%`);
		}
		if (vitals.Temp && vitals.Temp !== 0) {
			parts.push(`**Temp**: ${vitals.Temp}°C`);
		}
		return parts.length > 0 ? parts.join(" \n") : "-";
	};

	const renderOLDCART = (oldcart: OLDCART | null) => {
		if (
			!oldcart ||
			Object.values(oldcart).every(
				(value) => value === "" || value === null || value === undefined,
			)
		) {
			return "--";
		}

		const parts: string[] = [];
		if (oldcart.onset && oldcart.onset.trim() !== "") {
			parts.push(`**Onset:** ${oldcart.onset}`);
		}
		if (oldcart.location && oldcart.location.trim() !== "") {
			parts.push(`**Location:** ${oldcart.location}`);
		}
		if (oldcart.duration && oldcart.duration.trim() !== "") {
			parts.push(`**Duration:** ${oldcart.duration}`);
		}
		if (oldcart.character && oldcart.character.trim() !== "") {
			parts.push(`**Character:** ${oldcart.character}`);
		}
		if (oldcart.aggravating && oldcart.aggravating.trim() !== "") {
			parts.push(`**Aggravating:** ${oldcart.aggravating}`);
		}
		if (oldcart.relieving && oldcart.relieving.trim() !== "") {
			parts.push(`**Relieving:** ${oldcart.relieving}`);
		}
		if (oldcart.radiation && oldcart.radiation.trim() !== "") {
			parts.push(`**Radiation:** ${oldcart.radiation}`);
		}
		if (oldcart.timing && oldcart.timing.trim() !== "") {
			parts.push(`**Timing:** ${oldcart.timing}`);
		}
		if (oldcart.severity && oldcart.severity.trim() !== "") {
			parts.push(`**Severity:** ${oldcart.severity}`);
		}
		return parts.length > 0 ? parts.join("\n") : "-";
	};

	const renderArrayField = (items: string[]) => {
		if (!items || items.length === 0) {
			return "--";
		}
		const filteredItems = items.filter((item) => item && item.trim() !== "");
		return filteredItems.length > 0 ? filteredItems.join(", ") : "-";
	};

	const getFieldValue = (field: string) => {
		const dataKey = fieldMapping[field as keyof typeof fieldMapping];

		const alwaysShowFields = ["Chief Complaint", "Vitals"];
		const shouldAlwaysShow = showTooltip
			? true
			: alwaysShowFields.includes(field);

		if (
			!(isUserData || isAnswered) &&
			currentItemGoldStandard &&
			!shouldAlwaysShow
		) {
			const currentItemValue = (
				currentItemGoldStandard as Record<string, unknown>
			)[dataKey];
			if (currentItemValue !== null && currentItemValue !== undefined) {
				if (Array.isArray(currentItemValue) && currentItemValue.length === 0) {
					return "--";
				}
				if (typeof currentItemValue === "object" && currentItemValue !== null) {
					const hasData = Object.values(currentItemValue).some(
						(v) => v !== "" && v !== 0 && v !== null && v !== undefined,
					);
					if (hasData) {
						return "--";
					}
				} else if (currentItemValue !== "") {
					return "--";
				}
			}
		}

		switch (field) {
			case "Chief Complaint":
				return data.chiefComplaint || defaultGoldStandardData.chiefComplaint;
			case "Assessment":
				return data.assessment || "--";
			case "Vitals":
				return renderVitals(data.vitals || defaultGoldStandardData.vitals);
			case "OLDCART":
				return renderOLDCART(data.OLDCART || null);
			case "Risk Factors":
				return renderArrayField(data.riskFactors || []);
			case "ROS":
				return renderArrayField(data.ROS || []);
			case "Physical Exam":
				return renderArrayField(data.physicalExam || []);
			case "Plan":
				return renderArrayField(data.plan || []);
			case "Alerts":
				return renderArrayField(data.alerts || []);
			default:
				return "--";
		}
	};

	const allFields = [
		"Chief Complaint",
		"Vitals",
		"OLDCART",
		"Risk Factors",
		"ROS",
		"Physical Exam",
		"Differential Diagnosis",
		"Diagnosis",
		"Assessment",
		"Plan",
		"Alerts",
	];

	const tooltipMapping = {
		"Chief Complaint":
			"Learner should elicit and record the patient's symptom(s) in their own words, capturing sudden onset, pleuritic (worse with breathing) chest pain, and acute dyspnea. Correct recognition and phrasing demonstrates understanding of a high-acuity respiratory/cardiac complaint.",
		Vitals:
			"Learner should recognize and highlight abnormal findings: tachycardia (HR > 100), hypoxia (SpO₂ < 90%), tachypnea (RR > 20). Each abnormal vital should be flagged, as they indicate cardiorespiratory compromise. Scoring rewards correct interpretation and documentation of urgency/severity.",
		OLDCART:
			"Learner should systematically elicit each element of OLDCART. Scoring prioritizes rapid/sudden onset, pleuritic quality, constant duration, severity, and aggravation with breathing (classic for PE). Complete documentation reflects thorough and focused history-taking skills.",
		"Risk Factors":
			"Learner should ask about and identify VTE risk factors, including recent major surgery, immobilization, and signs of DVT (e.g., calf swelling/pain). The rubric rewards identification of both general (immobility, surgery) and specific (calf symptoms) risk factors for pulmonary embolism.",
		ROS: "Learner should actively screen for PE-associated symptoms (especially DVT signs, hemoptysis, syncope, chest tightness). Correct choices demonstrate knowledge of PE pathophysiology and ability to perform a targeted review of systems. Avoids irrelevant or generic symptoms that don't change clinical reasoning.",
		"Physical Exam":
			"Learner performs a focused exam of the cardiopulmonary system and extremities, specifically inspecting for signs of DVT. Correct findings include tachycardia, abnormal pulmonary sounds (if present), and unilateral leg changes. Full credit for combining relevant systems and interpreting abnormalities.",
		"Differential Diagnosis": "",
		Diagnosis: "",
		Plan: "Learner prioritizes emergency management: stabilization (ABCs, oxygen), rapid diagnostic workup (labs, ECG, imaging), and timely initiation of treatment (anticoagulation). Scoring rewards correct sequencing and rationale; partial credit if steps are present but poorly ordered or incomplete.",
		"Alerts/Flags": `Learner highlights all "red flags" for high-risk PE, including abnormal vitals, relevant history, and scoring criteria (Wells). Correct answers show understanding of risk stratification, urgency, and the need for rapid escalation or consultation if instability is present.`,
		Assessment:
			"Learner synthesizes key findings to support primary diagnosis and identifies severity.",
	};

	const fieldMapping = {
		"Chief Complaint": "chiefComplaint",
		Vitals: "vitals",
		OLDCART: "OLDCART",
		"Risk Factors": "riskFactors",
		ROS: "ROS",
		"Physical Exam": "physicalExam",
		"Differential Diagnosis": "differentialDiagnosis",
		Diagnosis: "diagnosis",
		Plan: "plan",
		Alerts: "alerts",
		Assessment: "assessment",
	};

	const fieldsSource = isUserData && goldStandardData ? goldStandardData : data;

	const alwaysShowFields = ["Chief Complaint", "Vitals"];

	const fields = allFields.filter((field) => {
		if (alwaysShowFields.includes(field)) {
			return true;
		}

		const dataKey = fieldMapping[field as keyof typeof fieldMapping];
		const value = (fieldsSource as Record<string, unknown>)[dataKey];

		if (value === null || value === undefined || value === "") {
			return false;
		}
		if (Array.isArray(value) && value.length === 0) {
			return false;
		}
		if (typeof value === "object" && value !== null) {
			const hasData = Object.values(value).some(
				(v) => v !== "" && v !== 0 && v !== null && v !== undefined,
			);
			return hasData;
		}
		return true;
	});

	const theme = useAppTheme();

	return (
		<DataTable
			style={{
				paddingBottom: 16,
			}}
		>
			<DataTable.Header>
				<DataTable.Title style={styles.fieldColumn}>Fields</DataTable.Title>
				<DataTable.Title style={styles.statusColumn}>
					Current Status
				</DataTable.Title>
			</DataTable.Header>

			{fields.map((field) => (
				<DataTable.Row
					key={field}
					// @ts-expect-error - cursor is not a valid prop for DataTable.Row
					style={{
						borderColor: theme.colors.divider,
						borderWidth: 1,
						borderLeftWidth: 0,
						borderRightWidth: 0,
						cursor: "default",
					}}
				>
					<DataTable.Cell style={styles.fieldColumn}>
						{showTooltip ? (
							<TooltipWrapper
								title={tooltipMapping[field as keyof typeof tooltipMapping]}
							>
								<Text variant="bodyMedium" numberOfLines={1}>
									{field}
								</Text>
							</TooltipWrapper>
						) : (
							<Text
								variant="bodyMedium"
								numberOfLines={1}
								style={{ cursor: "default" }}
							>
								{field}
							</Text>
						)}
					</DataTable.Cell>
					<DataTable.Cell style={styles.statusColumn}>
						<Text
							numberOfLines={10}
							style={{
								paddingVertical: 8,
								cursor: "default",
							}}
						>
							<MarkdownRenderer text={getFieldValue(field)} />
						</Text>
					</DataTable.Cell>
				</DataTable.Row>
			))}
		</DataTable>
	);
};

const styles = StyleSheet.create({
	fieldColumn: {
		flex: 2.3,
	},
	statusColumn: {
		flex: 4,
		// paddingVertical: 8,
	},
});

export default EMRTable;
